import { yupResolver } from '@hookform/resolvers/yup';
import {
  Dialog, DialogActions, DialogContent,
} from '@mui/material';
import { GridRowsProp } from '@mui/x-data-grid';
import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import CustomButton from 'components/CustomButton';
import GenericConfirmationDialog from 'components/GenericConfirmationDialog';
import { MOVE_GRID_COLUMNS, MOVE_INVENTORY_DEFAULT_VALUES } from 'containers/Inventory/constants';
import DialogHeader from 'containers/Inventory/DialogHeader';
import AreaSelector from 'containers/Inventory/MoveInventory/AreaSelector';
import SelectedItemsTable from 'containers/Inventory/SelectedItemsTable';
import { useInventoryContext } from 'context/InventoryContext';

import { putMoveInventory } from 'services/Inventory';

import { InventoryCustomAction } from 'constants/enums';
import { InventoryCustomActionPayload } from 'types/inventory.interface';
import { handleApiErrors } from 'utils/errorUtils';
import { getStoreId } from 'utils/storageUtils';
import { MoveInventoryFormData, moveInventorySchema } from 'validation/inventorySchema';

interface MoveInventoryDialogProps {
    selectedRows: GridRowsProp;
    isOpen: boolean;
    onClose: (refetch: boolean) => void;
}

const MoveInventory: FC<MoveInventoryDialogProps> = ({
  selectedRows,
  isOpen,
  onClose,
}) => {
  const [selectedArea, setSelectedArea] = useState<string>('');
  const [moveQuantities, setMoveQuantities] = useState<Record<string, number>>({});
  const { areas } = useInventoryContext();
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleMoveQuantityChange = (e: { itemId: string; value: number }) => {
    setMoveQuantities((prev) => ({ ...prev, [e.itemId]: e.value }));
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<MoveInventoryFormData>({
    defaultValues: MOVE_INVENTORY_DEFAULT_VALUES,
    resolver: yupResolver(moveInventorySchema),
  });

  const onSubmit = async () => {
    try {
      setIsLoading(true);
      const clientId = getStoreId();
      if (!clientId) {
        throw new Error('Client ID not found');
      }
      const changedRows = selectedRows.filter((row) => {
        if (moveQuantities[row.itemId] === undefined) {
          return true;
        }
        const updated = moveQuantities[row.itemId];
        if (updated > Number(row.available) || updated <= 0) {
          return false;
        }
        return true;
      });

      if (changedRows.length === 0) {
        toast.warning('Please make sure you are trying to move the correct quantity of each item.');
        return;
      }

      const movePayload: InventoryCustomActionPayload = {
        type: InventoryCustomAction.MOVE,
        destiantionAreaId: selectedArea,
        items: changedRows.map((row) => {
          let quantity = moveQuantities[row.itemId];
          if (quantity === undefined) {
            quantity = row.available;
          }
          return {
            itemId: row.itemId,
            batchId: row.batchId,
            areaId: row.areaId,
            quantity,
          };
        }),
      };

      await putMoveInventory(clientId, movePayload);
      toast.success('Inventory moved successfully');
    } catch (e) {
      handleApiErrors(e);
    } finally {
      onClose(true);
      setIsLoading(false);
    }
  };

  const confirmDialog = (data: MoveInventoryFormData) => {
    setSelectedArea(data.selectedArea);
    setOpenConfirmationDialog(true);
  };

  const closeDialogWithoutRefetch = () => {
    onClose(false);
  };

  return (
    <Dialog
      open={isOpen}
      fullWidth
      onClose={closeDialogWithoutRefetch}
      aria-describedby="select-adjust-reason"
      slotProps={{
        paper: { sx: { maxWidth: 900, borderRadius: '16px', p: 2.5 } },
      }}
    >
      <DialogHeader title="Move Inventory" onClose={closeDialogWithoutRefetch} />
      <DialogContent>
        <AreaSelector
          areas={areas}
          control={control}
          errors={errors}
        />
        <SelectedItemsTable
          rows={selectedRows}
          columns={MOVE_GRID_COLUMNS(moveQuantities, handleMoveQuantityChange)}
          customAction={InventoryCustomAction.MOVE}
        />
      </DialogContent>
      <DialogActions sx={{ p: '0 24px 24px' }}>
        <CustomButton
          id="button-cancelMoveInventory"
          variant="outlined"
          onClick={closeDialogWithoutRefetch}
        >
          Cancel
        </CustomButton>
        <CustomButton
          id="button-confirmMoveInventory"
          variant="contained"
          onClick={handleSubmit(confirmDialog)}
        >
          Confirm
        </CustomButton>
      </DialogActions>
      {openConfirmationDialog && (
        <GenericConfirmationDialog
          isOpen={openConfirmationDialog}
          dialogMessage="Are you sure you want to move the inventory?"
          onClose={() => setOpenConfirmationDialog(false)}
          onSubmit={onSubmit}
          isLoading={isLoading}
        />
      )}
    </Dialog>
  );
};

export default MoveInventory;
